import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  useTheme,
  Divider,
  alpha,
  Chip,
  Tooltip,
  IconButton,
  Avatar,
  Paper,
  LinearProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NatureIcon from '@mui/icons-material/Nature';
import SpaIcon from '@mui/icons-material/Spa';
import ParkIcon from '@mui/icons-material/Park';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsightsIcon from '@mui/icons-material/Insights';
import BoltIcon from '@mui/icons-material/Bolt';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NextWeekIcon from '@mui/icons-material/NextWeek';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import HistoryIcon from '@mui/icons-material/History';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Import from our business service
import { 
  getBusiness, 
  getCarbonImpact, 
  getBadges,
  getEnergyOverview,
  getEnergyInsights,
  getUsageForecast,
  getPeakDemand
} from '../services/business';

import { 
  Business, 
  CarbonImpactData, 
  Badge,
  EnergyOverviewData,
  EnergyInsightsData,
  UsageForecastData,
  PeakDemandData
} from '../services/business';

interface OverviewProps {
  businessId: string;
  timeFrame?: number;
}

// Impact types configuration
const impactTypes = [
  { id: 'trees', label: 'Trees Planted', icon: <ParkIcon fontSize="large" />, color: '#10B981', unit: 'trees', description: 'grown for 10 years' },
  { id: 'cars', label: 'Car Miles', icon: <DirectionsCarIcon fontSize="large" />, color: '#3B82F6', unit: 'miles', description: 'not driven' },
  { id: 'coal', label: 'Coal Saved', icon: <BubbleChartIcon fontSize="large" />, color: '#475569', unit: 'lbs', description: 'of coal not burned' },
  { id: 'phones', label: 'Smartphones', icon: <BatteryChargingFullIcon fontSize="large" />, color: '#FF7A00', unit: 'phones', description: 'fully charged' },
];

// Energy metric card component
interface EnergyMetricCardProps {
  title: string;
  value: string | number;
  unit: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  trend?: number;
}

const EnergyMetricCard = ({ title, value, unit, subtitle, icon, color, trend }: EnergyMetricCardProps) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        border: `1px solid ${alpha(color, 0.2)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
          duration: theme.transitions.duration.shorter
        }),
        '&:hover': {
          boxShadow: `0px 8px 24px ${alpha(color, 0.15)}`,
          transform: 'translateY(-4px)',
          borderColor: alpha(color, 0.4),
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '25%',
          height: '25%',
          background: `radial-gradient(circle at top right, ${alpha(color, 0.15)}, transparent 70%)`,
          zIndex: 0,
        }}
      />
      
      <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            {title}
          </Typography>
          <Avatar 
            sx={{ 
              width: 36, 
              height: 36, 
              bgcolor: alpha(color, 0.15),
              color: color,
            }}
          >
            {icon}
          </Avatar>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="h4" fontWeight={700} sx={{ mr: 1 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {unit}
          </Typography>
          
          {trend !== undefined && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                ml: 1.5, 
                color: trend >= 0 ? theme.palette.success.main : theme.palette.error.main,
                bgcolor: trend >= 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                borderRadius: 1,
                px: 0.8,
                py: 0.3,
              }}
            >
              {trend >= 0 ? (
                <ArrowUpwardIcon fontSize="small" sx={{ fontSize: 14, mr: 0.3 }} />
              ) : (
                <ArrowDownwardIcon fontSize="small" sx={{ fontSize: 14, mr: 0.3 }} />
              )}
              <Typography variant="caption" fontWeight={600}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Overview = ({ businessId, timeFrame = 12 }: OverviewProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Add currentImpactIndex state for the environmental impact cycler
  const [currentImpactIndex, setCurrentImpactIndex] = useState(0);
  
  // States for data
  const [business, setBusiness] = useState<Business | null>(null);
  const [carbonImpact, setCarbonImpact] = useState<CarbonImpactData | null>(null);
  const [energyOverview, setEnergyOverview] = useState<EnergyOverviewData | null>(null);
  const [energyInsights, setEnergyInsights] = useState<EnergyInsightsData | null>(null);
  const [usageForecast, setUsageForecast] = useState<UsageForecastData | null>(null);
  const [peakDemand, setPeakDemand] = useState<PeakDemandData | null>(null);
  const [badges, setBadges] = useState<Badge[]>([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auto-rotate through impact types
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImpactIndex((prevIndex) => (prevIndex + 1) % impactTypes.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch business info
        const businessData = await getBusiness(businessId);
        setBusiness(businessData);
        
        // Fetch carbon impact data
        const impactData = await getCarbonImpact(businessId);
        setCarbonImpact(impactData);
        
        // Fetch energy overview
        const overviewData = await getEnergyOverview(businessId);
        setEnergyOverview(overviewData);
        
        // Fetch energy insights
        const insightsData = await getEnergyInsights(businessId);
        setEnergyInsights(insightsData);
        
        // Fetch usage forecast
        const forecastData = await getUsageForecast(businessId);
        setUsageForecast(forecastData);
        
        // Fetch peak demand
        try {
          const peakDemandData = await getPeakDemand(businessId);
          setPeakDemand(peakDemandData);
        } catch (error) {
          console.error('Error fetching peak demand data:', error);
          // Continue with other data if peak demand fails
        }
        
        // Fetch badges
        const badgeData = await getBadges(businessId);
        setBadges(badgeData);
        
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId, timeFrame]);

  // Helper function to get the impact value based on the current impact type
  const getImpactValue = () => {
    if (!carbonImpact) return 0;
    
    switch (impactTypes[currentImpactIndex].id) {
      case 'trees':
        return carbonImpact.treesEquivalent;
      case 'cars':
        return carbonImpact.carMilesEquivalent;
      case 'coal':
        return Math.round(carbonImpact.coalSaved);
      case 'phones':
        return carbonImpact.smartphonesCharged;
      default:
        return 0;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        bgcolor: alpha(theme.palette.error.main, 0.1),
        borderRadius: 2,
        color: theme.palette.error.main 
      }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header - Simplified */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome Back, <Box 
            component="span" 
            sx={{ 
              fontFamily: "'Pacifico', cursive", 
              background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
              display: 'inline-block',
              transform: 'rotate(-2deg)',
              position: 'relative',
              top: '2px'
            }}
          >
            Green Earth Cafe!
          </Box>
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Your sustainability journey is making a difference. Here's an overview of your energy performance and environmental impact.
        </Typography>
      </Box>
      
      {/* Energy Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
            Energy Summary
          </Typography>
          <Divider />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <EnergyMetricCard
            title="Last Month's Usage"
            value={energyOverview?.lastMonthKwh || 0}
            unit="kWh"
            subtitle={`Usage for ${energyOverview?.lastMonthName || 'February 2025'}`}
            icon={<ElectricBoltIcon />}
            color="#FF7A00"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <EnergyMetricCard
            title="Monthly Average"
            value={energyOverview?.monthlyAvgKwh || 0}
            unit="kWh"
            subtitle="Average monthly usage (all available data)"
            icon={<HistoryIcon />}
            color="#3B82F6"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <EnergyMetricCard
            title="Current Month"
            value={energyOverview?.thisMonthSoFarKwh || 0}
            unit="kWh"
            subtitle={`Usage so far in ${energyOverview?.thisMonthName || 'March 2025'}`}
            icon={<CalendarTodayIcon />}
            color="#10B981"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <EnergyMetricCard
            title="This Month's Forecast"
            value={usageForecast?.currentMonth?.forecastKwh || 0}
            unit="kWh"
            subtitle={`Forecast for ${usageForecast?.currentMonth?.name || 'March 2025'}`}
            icon={<NextWeekIcon />}
            color="#8B5CF6"
          />
        </Grid>
      </Grid>
      
      {/* Carbon Impact and Peak Times */}
      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
            Environmental Impact
          </Typography>
          <Divider />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* CO2 Emissions Avoided Card */}
            <Card 
              sx={{ 
                mb: 3,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${alpha('#10B981', 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
                  duration: theme.transitions.duration.standard
                }),
                '&:hover': {
                  boxShadow: `0px 12px 28px ${alpha('#10B981', 0.18)}`,
                  transform: 'translateY(-4px)',
                  borderColor: alpha('#10B981', 0.3),
                }
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#10B981' }}>
                    CO2 Emissions Avoided
                  </Typography>
                  <Avatar
                    sx={{
                      bgcolor: alpha('#10B981', 0.15),
                      color: '#10B981',
                    }}
                  >
                    <NatureIcon />
                  </Avatar>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="h3" fontWeight={700} sx={{ mb: 0.5, color: '#10B981' }}>
                      {carbonImpact?.totalCO2Avoided.toLocaleString()} <Typography component="span" variant="h6" sx={{ color: '#10B981' }}>lbs</Typography>
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      CO₂ emissions avoided since becoming a Green P100NEER
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        mt: 1,
                        color: '#10B981',
                        borderColor: '#10B981',
                        transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                          duration: theme.transitions.duration.short
                        }),
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 8px ${alpha('#10B981', 0.25)}`,
                          borderColor: '#10B981',
                          backgroundColor: alpha('#10B981', 0.05),
                        }
                      }}
                      onClick={() => navigate('/carbon-impact')}
                    >
                      View Full Impact
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={7}>
                    <Divider orientation="vertical" sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', left: 0, top: 0, height: '100%' }} />
                    <Box sx={{ pl: { xs: 0, md: 2 }, mt: { xs: 2, md: 0 } }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                        This is equivalent to:
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(impactTypes[currentImpactIndex].color, 0.1),
                          transition: 'background-color 0.5s ease',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: alpha(impactTypes[currentImpactIndex].color, 0.15),
                            color: impactTypes[currentImpactIndex].color,
                            width: 45,
                            height: 45,
                            mr: 2,
                          }}
                        >
                          {impactTypes[currentImpactIndex].icon}
                        </Avatar>
                        
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {getImpactValue().toLocaleString()} {impactTypes[currentImpactIndex].unit}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {impactTypes[currentImpactIndex].description}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                        {impactTypes.map((impact, index) => (
                          <Box
                            key={impact.id}
                            component="span"
                            onClick={() => setCurrentImpactIndex(index)}
                            sx={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              mx: 0.5,
                              borderRadius: '50%',
                              cursor: 'pointer',
                              bgcolor: index === currentImpactIndex ? impact.color : alpha(impact.color, 0.3),
                              transition: 'all 0.3s ease',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Increase Your Positive Impact Box */}
            <Box 
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.secondary.main, 0.85)} 100%)`,
                color: '#ffffff',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                overflow: 'hidden',
                position: 'relative',
                mt: 'auto',
                transition: theme.transitions.create(['box-shadow', 'transform'], {
                  duration: theme.transitions.duration.standard
                }),
                '&:hover': {
                  boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-4px)',
                }
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  bgcolor: alpha('#ffffff', 0.1),
                  zIndex: 0
                }}
              />
              <Box 
                sx={{ 
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '180px',
                  height: '180px',
                  borderRadius: '50%',
                  bgcolor: alpha('#ffffff', 0.07),
                  zIndex: 0
                }}
              />
              
              <Box sx={{ zIndex: 1, width: '70%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Know a business owner not yet on 100% renewable energy?
                </Typography>
                <Typography variant="body2" sx={{ mb: 0, opacity: 0.9 }}>
                  Help them become a Green P100NEER!
                </Typography>
              </Box>
              
              <Button 
                variant="contained" 
                component="a"
                href="https://www.ocpower.org/energy-programs/green-p100neer-program/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  zIndex: 1,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  bgcolor: '#ffffff',
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                    duration: theme.transitions.duration.short
                  }),
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.9),
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                  }
                }}
              >
                Learn More
              </Button>
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: `0px 12px 28px ${alpha(theme.palette.warning.main, 0.18)}`,
                transform: 'translateY(-4px)',
                borderColor: alpha(theme.palette.warning.main, 0.3),
              }
            }}
          >
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="h6" fontWeight={600}>
                  Peak Energy Times
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.main,
                  }}
                >
                  <AccessTimeIcon />
                </Avatar>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Top 3 peak energy hours in the last 30 days:
              </Typography>
              
              <Grid container spacing={1.5}>
                {(peakDemand?.last_30_days_top_peaks || []).map((peak, index) => (
                  <Grid item xs={12} key={index}>
                    <Box 
                      sx={{ 
                        p: 1.25, 
                        borderRadius: 2, 
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: theme.transitions.create(['background-color', 'border-color', 'transform'], {
                          duration: theme.transitions.duration.shortest
                        }),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.08),
                          borderColor: alpha(theme.palette.warning.main, 0.2),
                          transform: 'scale(1.01)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.warning.main, 0.15),
                            color: theme.palette.warning.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            mr: 2,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {peak.formatted_hour}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {peak.formatted_date}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={`${peak.kwh.toFixed(3)} kWh`}
                        sx={{ 
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.warning.main, 0.15),
                          color: theme.palette.warning.main,
                          borderRadius: 1,
                        }} 
                      />
                    </Box>
                  </Grid>
                ))}
                
                {(!peakDemand || peakDemand.last_30_days_top_peaks.length === 0) && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 3, 
                      textAlign: 'center', 
                      bgcolor: alpha(theme.palette.warning.main, 0.05),
                      borderRadius: 2
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Peak demand data is not available yet. Please check back later.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    borderRadius: 4,
                    transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                      duration: theme.transitions.duration.short
                    }),
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 8px ${alpha(theme.palette.warning.main, 0.25)}`,
                    }
                  }}
                  onClick={() => navigate('/insights')}
                >
                  View Detailed Analysis
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Achievements and Impact Equivalents */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
            Achievements
          </Typography>
          <Divider />
        </Grid>
        
        <Grid item xs={12}>
          <Card
            sx={{
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: `0px 12px 28px ${alpha(theme.palette.primary.main, 0.1)}`,
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  Your Green Achievements
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                      duration: theme.transitions.duration.short
                    }),
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                    }
                  }}
                  onClick={() => navigate('/achievements')}
                >
                  View All
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                {badges.length > 0 ? badges.slice(0, 3).map((badge, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        textAlign: 'center', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha(
                          index === 0 ? '#10B981' : 
                          (index === 1 ? '#F59E0B' : '#3B82F6'), 
                          0.2
                        ),
                        bgcolor: alpha(
                          index === 0 ? '#10B981' : 
                          (index === 1 ? '#F59E0B' : '#3B82F6'), 
                          0.05
                        ),
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: theme.transitions.create(['box-shadow', 'transform', 'border-color', 'background-color'], {
                          duration: theme.transitions.duration.short
                        }),
                        '&:hover': {
                          boxShadow: `0px 8px 16px ${alpha(
                            index === 0 ? '#10B981' : 
                            (index === 1 ? '#F59E0B' : '#3B82F6'), 
                            0.15
                          )}`,
                          transform: 'translateY(-4px)',
                          borderColor: alpha(
                            index === 0 ? '#10B981' : 
                            (index === 1 ? '#F59E0B' : '#3B82F6'), 
                            0.4
                          ),
                          bgcolor: alpha(
                            index === 0 ? '#10B981' : 
                            (index === 1 ? '#F59E0B' : '#3B82F6'), 
                            0.08
                          )
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          mb: 2,
                          bgcolor: alpha(
                            index === 0 ? '#10B981' : 
                            (index === 1 ? '#F59E0B' : '#3B82F6'), 
                            0.15
                          ),
                          color: index === 0 ? '#10B981' : 
                                 (index === 1 ? '#F59E0B' : '#3B82F6'),
                        }}
                      >
                        {index === 0 ? <SpaIcon fontSize="large" /> : 
                         (index === 1 ? <BoltIcon fontSize="large" /> : 
                         <EmojiEventsIcon fontSize="large" />)}
                      </Avatar>
                      
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {badge.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {badge.description}
                      </Typography>
                    </Paper>
                  </Grid>
                )) : (
                  <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                    <Typography variant="body1" color="text.secondary">
                      No badges earned yet. Continue your sustainability journey to earn badges!
                    </Typography>
                  </Box>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Overview; 